/**
 * Admin Email Dashboard — /app/admin/email
 * Metrics · queue · list · retry · preview · send test.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Mail, RefreshCw, Search, Play, Send, FlaskConical, RotateCcw, Eye, X,
} from "lucide-react";

const STATUSES = ["queued", "sending", "sent", "failed", "skipped_by_preference"];

export default function AdminEmail() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ status: "", category: "", template_key: "", q: "" });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testCtx, setTestCtx] = useState({ template_key: "", to_user_id: "" });

  const load = async () => {
    setBusy(true);
    try {
      const [s, l, t] = await Promise.all([
        api.get("/notifications/admin/summary"),
        api.get(`/notifications/admin/list?${new URLSearchParams(Object.entries(filters).filter(([, v]) => v))}&limit=50`),
        api.get("/notifications/admin/templates"),
      ]);
      setSummary(s.data);
      setItems(l.data.items || []);
      setTemplates(t.data.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  if (user && !user.is_admin) return <Navigate to="/app" replace />;

  const dispatch = async () => {
    try {
      const { data } = await api.post("/notifications/admin/dispatch-now");
      toast.success(`Worker tick · sent=${data.stats.sent} failed=${data.stats.failed} retried=${data.stats.retried}`);
      load();
    } catch (e) { toast.error("Dispatch failed"); }
  };

  const retry = async (id) => {
    try {
      await api.post(`/notifications/admin/retry/${id}`);
      toast.success("Retried");
      load();
    } catch (e) { toast.error("Retry failed"); }
  };

  const showPreview = async (key) => {
    try {
      const { data } = await api.get(`/notifications/admin/preview/${key}`);
      setPreview({ key, ...data });
    } catch (e) { toast.error("Preview failed"); }
  };

  const sendTest = async () => {
    if (!testCtx.template_key) return toast.error("Pick a template");
    try {
      const { data } = await api.post("/notifications/admin/send-test", {
        template_key: testCtx.template_key,
        to_user_id: testCtx.to_user_id || undefined,
      });
      toast.success(`Test enqueued + dispatched · sent=${data.dispatch_stats?.sent || 0}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="max-w-7xl" data-testid="admin-email-dashboard">
      <PageHeader
        eyebrow="Admin · Email"
        title="Notification Engine"
        subtitle="Queue, deliveries, opens, clicks, bounces — all in one place."
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
          <Stat label="Total sent" value={summary.total_sent} testid="stat-sent" />
          <Stat label="Last 30d" value={summary.last_30_days} testid="stat-30d" />
          <Stat label="Queue" value={summary.queue_depth} testid="stat-queue" tone={summary.queue_depth > 50 ? "warn" : ""} />
          <Stat label="Delivery" value={`${summary.delivery_rate_pct}%`} testid="stat-delivery" />
          <Stat label="Open" value={`${summary.open_rate_pct}%`} testid="stat-open" />
          <Stat label="Click" value={`${summary.click_rate_pct}%`} testid="stat-click" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" onClick={dispatch} data-testid="btn-dispatch"><Play size={12} className="mr-1" /> Dispatch now</Button>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="btn-refresh"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /></Button>
      </div>

      {/* Send Test */}
      <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="send-test-panel">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><FlaskConical size={12} /> Send a test</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={testCtx.template_key} onChange={(e) => setTestCtx({ ...testCtx, template_key: e.target.value })} className="bg-background border border-border rounded-md px-3 py-2 text-sm" data-testid="test-template">
            <option value="">— pick template —</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.key}</option>)}
          </select>
          <input value={testCtx.to_user_id} onChange={(e) => setTestCtx({ ...testCtx, to_user_id: e.target.value })} placeholder="user_id (optional, defaults to you)" className="flex-1 min-w-[240px] bg-background border border-border rounded-md px-3 py-2 text-sm" data-testid="test-user-id" />
          <Button size="sm" onClick={sendTest} data-testid="btn-send-test"><Send size={12} className="mr-1" /> Send</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search user_id, email, or msg_id…" className="w-full pl-9 pr-3 py-1.5 bg-background border border-border rounded-md text-sm outline-none" />
        </div>
        <Select value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} label="Status" options={STATUSES} testid="filter-status" />
        <Select value={filters.template_key} onChange={(v) => setFilters({ ...filters, template_key: v })} label="Template" options={templates.map((t) => t.key)} testid="filter-template" />
      </div>

      {/* Templates list w/ previews */}
      <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="templates-grid">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Templates ({templates.length})</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
          {templates.map((t) => (
            <button key={t.key} onClick={() => showPreview(t.key)} className="text-left border border-border rounded-lg p-3 hover:border-primary/40 transition-colors" data-testid={`template-${t.key}`}>
              <div className="text-sm font-medium truncate">{t.key}</div>
              <div className="text-[10px] text-muted-foreground mt-1 truncate">{t.category}</div>
              <div className="text-[11px] text-muted-foreground mt-1 truncate">{t.subject}</div>
              <div className="text-[9px] mt-2 flex items-center gap-1 text-primary"><Eye size={10} /> preview</div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent items */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="items-table">
        <div className="p-3 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">Recent sends</div>
        {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No notifications match.</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 px-3 py-2 border-b border-border text-sm" data-testid={`item-${it.id}`}>
            <StatusPill status={it.status} />
            <div className="flex-1 min-w-0">
              <div className="truncate"><b>{it.template_key}</b> <span className="text-muted-foreground">→ {it.payload?.to_email || it.user_id}</span></div>
              <div className="text-[10px] text-muted-foreground font-mono truncate">{it.id} · {it.category} · attempts={it.attempts} · {new Date(it.created_at).toLocaleString()}</div>
              {it.error && <div className="text-[10px] text-destructive">error: {it.error}</div>}
            </div>
            {(it.status === "failed" || it.status === "sent") && (
              <Button size="sm" variant="outline" onClick={() => retry(it.id)} data-testid={`btn-retry-${it.id}`}><RotateCcw size={10} /></Button>
            )}
          </div>
        ))}
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-border flex items-center gap-3">
              <div className="flex-1">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{preview.key} · {preview.category}</div>
                <div className="text-sm font-semibold">{preview.subject}</div>
              </div>
              <button onClick={() => setPreview(null)} className="p-1 rounded hover:bg-muted" data-testid="preview-close"><X size={16} /></button>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="flex-1 w-full bg-white" data-testid="preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid, tone }) {
  const toneCls = tone === "warn" ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))/0.06]" : "border-border";
  return (
    <div className={`border rounded-xl p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-black mt-1 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: "bg-emerald-500/15 text-emerald-500",
    queued: "bg-blue-500/15 text-blue-500",
    sending: "bg-amber-500/15 text-amber-500",
    failed: "bg-destructive/15 text-destructive",
    skipped_by_preference: "bg-muted text-muted-foreground",
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase ${map[status] || "bg-muted text-muted-foreground"}`}>{status}</span>;
}

function Select({ value, onChange, label, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-background border border-border rounded-md px-2 py-1.5 text-xs outline-none">
      <option value="">{label}: any</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
